import { applySnapshot, flow, getSnapshot, types } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { getConvertedDuration } from "utils/calculation";

export const MovementModel = types
  .model({
    id: types.number,
    title: types.string,
    description: types.maybe(types.string),
    duration: types.maybe(types.number),
    imageUrl: types.maybe(types.string),
    videoUrl: types.maybe(types.string),
  })
  .views(self => ({
    get durationText() {
      return getConvertedDuration(self.duration);
    },

    get formData() {
      return {
        ...self,
        file: self.imageUrl,
        durationMin: self.duration ? Math.trunc(self.duration / 60) : 0,
        durationSec: self.duration ? self.duration % 60 : 0,
      };
    },
  }));

const ContentStudioMovementsStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/workout/movement/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: MovementModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(MovementModel),
    isMoreLoading: types.optional(types.boolean, false),
    isMovementLoading: types.optional(types.boolean, false),
    isMovementManaging: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get movementsOptions() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, duration, imageUrl }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          durationText: getConvertedDuration(duration),
        },
      }));
    },
  }))
  .actions(self => ({
    fetchMoreMovements: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          const { orderBy, orderByDesc } = self.orderContext || {};

          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/workout/movement/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
            search: self.search,
            orderBy,
            orderByDesc,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    fetchMovementDetails: flow(function*(id: number) {
      self.isMovementLoading = true;

      try {
        const details = yield api.get(`/workout/movement/${id}`);

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMovementLoading = false;
      }
    }),

    createMovement: flow(function*(payload: any) {
      self.isMovementManaging = true;

      try {
        const { id } = yield api.postFormData(`/workout/movement`, payload);
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMovementManaging = false;
      }
    }),

    editMovementInfo: flow(function*(payload: any) {
      self.isMovementManaging = true;

      try {
        return yield api.put(`/workout/movement/${payload.id}`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMovementManaging = false;
      }
    }),

    editMovementImage: flow(function*(payload: any) {
      self.isMovementManaging = true;

      try {
        return yield api.putFormData(
          `/workout/movement/${payload.id}/photo/upload`,
          payload,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMovementManaging = false;
      }
    }),

    archiveMovement: flow(function*(id: number) {
      self.isMovementManaging = true;

      try {
        return yield api.post(`/workout/movement/${id}/archive`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMovementManaging = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export const ContentStudioArchivedMovementsStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/workout/movement/archived/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: MovementModel,
  searchBy: "search",
})
  .props({
    isManaging: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get options() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, duration, imageUrl }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          duration: getConvertedDuration(duration),
        },
      }));
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/workout/movement/archived/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    restore: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        return yield api.post(`/workout/movement/restore`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),
  }))
  .create();

export default ContentStudioMovementsStore.create();
